/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AwvInfo, MapNodeType, MapEdgeType, ResultTaskList } from "client";
import { SideDrawer } from "components/Common/SideDrawer";
import { AwvMapOverlay } from "components/Map/AwvMapOverlay";
import { Map, MapProps } from "components/Map/Map";
import {
  RoadClosureDetail,
  RoadClosureDetailProps,
} from "components/Map/RoadClosureDetail";
import {
  HighlightEdgePolygon,
  NodeMapOverlay,
} from "components/Map/NodeMapOverlay";
import { ResetMapHeading } from "components/Map/ResetMapHeading";
import { ResetMapPosition } from "components/Map/ResetMapPosition";
import { UserLocation } from "components/Map/UserLocation";
import { useMapDataContext } from "context/MapDataProvider";
import { useUserContext } from "context/UserContext";
import { FC, useCallback, useMemo, useState } from "react";
import { MultipleSingle } from "components/Multiple/MultipleSingle";
import { OfflineAwvMapOverlay } from "components/Map/OfflineAwvMapOverlay";
import { convertLatLngFromGeolocation, getElementType } from "utils/Map";
import { useAwvDataContext } from "context/AwvDataProvider";
import { ChargerMapOverlay } from "components/Map/ChargerMapOverlay";
import { useChargers } from "hooks/GetChargersHook";

export const Home: FC = () => {
  const user = useUserContext();
  const { mapData, setSelectedRouteElement } = useMapDataContext();
  const { awvData } = useAwvDataContext();

  const [isTilesLoaded, setIsTilesLoaded] = useState(false);
  // Memoization to prevent re-rendering
  const mapProps = useMemo<MapProps>(() => {
    return {
      mapContainerStyle: {
        height: "100%",
        width: "100%",
      },
      options: {
        disableDefaultUI: true,
        mapTypeControl: true,
        zoomControl: true,
        gestureHandling: "greedy",
        keyboardShortcuts: false,
        mapId: process.env.REACT_APP_GOOGLEMAP_MAPID,
      },
      center: {
        lat: user.jobsite?.latitude ?? 0,
        lng: user.jobsite?.longitude ?? 0,
      },
      zoom: user.jobsite?.zoom ?? 11,
      onTilesLoaded: () => {
        setIsTilesLoaded(true);
      },
    };
  }, [user.jobsite?.latitude, user.jobsite?.longitude, user.jobsite?.zoom]);

  // selected object show
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<
    MapNodeType | MapEdgeType | undefined
  >();
  const [roadClosureProps, setRoadClosureProps] =
    useState<RoadClosureDetailProps>();
  const [selectedAwv, setSelectedAwv] = useState<AwvInfo | undefined>();
  const [runningTask, setRunningTask] = useState<ResultTaskList | undefined>();

  const handleAwvSelect = useCallback(
    (awvInfo: AwvInfo) => {
      if (awvInfo.frameTypeAndNumber === selectedAwv?.frameTypeAndNumber) {
        // unselect
        setSelectedNode(undefined);
        setSelectedRouteElement(undefined);
        setRoadClosureProps(undefined);
        setSelectedAwv(undefined);
        setRunningTask(undefined);
        setSidebarOpen(false);
      } else {
        setSelectedNode(undefined);
        setSelectedRouteElement(undefined);
        setRoadClosureProps(undefined);
        setSelectedAwv(awvInfo);
        setRunningTask(undefined);
        setSidebarOpen(true);
      }
    },
    [selectedAwv?.frameTypeAndNumber, setSelectedRouteElement]
  );

  const handleSideClose = useCallback(() => {
    // unselect
    setSelectedNode(undefined);
    setSelectedRouteElement(undefined);
    setRoadClosureProps(undefined);
    setSelectedAwv(undefined);
    setRunningTask(undefined);
    setSidebarOpen(false);
  }, [setSelectedRouteElement]);

  const getRoadClosureProps = useCallback(
    (edge: MapEdgeType): RoadClosureDetailProps | undefined => {
      if (mapData.mapInfo) {
        const mapNodes = mapData.mapInfo.flatMap((floor) => floor.nodes);
        // find start position
        const start = convertLatLngFromGeolocation(
          mapNodes?.find((node) => node?.id === edge.startNodeId)?.geolocation
        );
        // find end position
        const end = convertLatLngFromGeolocation(
          mapNodes?.find((node) => node?.id === edge.endNodeId)?.geolocation
        );
        if (!start || !end) {
          // node not found.
          return undefined;
        }
        return { start: start, end: end };
      }
    },
    [mapData.mapInfo]
  );

  const handleNodeSelect = useCallback(
    (node: MapNodeType | MapEdgeType) => {
      if (getElementType(node) === "Edge" && node.priority === 0) {
        const closureProps = getRoadClosureProps(node as MapEdgeType);
        setSelectedAwv(undefined);
        setRunningTask(undefined);
        setSelectedNode((current) => {
          if (node.id === current?.id || !closureProps) {
            // unselect
            setSelectedRouteElement(undefined);
            setRoadClosureProps(undefined);
            setSidebarOpen(false);
            return undefined;
          } else {
            setSelectedRouteElement(node);
            setRoadClosureProps(closureProps);
            setSidebarOpen(true);
            return node;
          }
        });
      }
    },
    [getRoadClosureProps, setSelectedRouteElement]
  );

  const chargers = useChargers();

  if (mapData.mapVer === 0) {
    // When no MAP data.
    return <></>;
  }

  return (
    <Container>
      <Map {...mapProps}>
        <UserLocation />
        {isTilesLoaded && (
          <>
            <ResetMapPosition center={mapProps.center!} zoom={mapProps.zoom!} />
            <ResetMapHeading />
            <OfflineAwvMapOverlay
              awvInfos={awvData}
              onAwvSelect={handleAwvSelect}
              selected={
                selectedAwv?.frameTypeAndNumber
                  ? [selectedAwv.frameTypeAndNumber]
                  : undefined
              }
            />
          </>
        )}
        <NodeMapOverlay
          mapData={mapData}
          onNodeSelect={handleNodeSelect}
          selected={selectedNode}
        />
        <AwvMapOverlay
          awvInfos={awvData}
          onAwvSelect={handleAwvSelect}
          selected={
            selectedAwv?.frameTypeAndNumber
              ? [selectedAwv.frameTypeAndNumber]
              : undefined
          }
        />
        <ChargerMapOverlay chargers={chargers.value} />
        {selectedAwv && runningTask && (
          <HighlightEdgePolygon
            mapData={mapData}
            highlightEdges={runningTask.taskActualRouteInfo ?? []}
            currentNodeId={parseInt(selectedAwv.currentNode!)}
          />
        )}
      </Map>
      <SideDrawer open={sidebarOpen} onClose={handleSideClose}>
        <>
          {roadClosureProps && <RoadClosureDetail {...roadClosureProps} />}
          {selectedAwv && (
            <MultipleSingle
              awvInfo={selectedAwv}
              setRunningTask={setRunningTask}
            />
          )}
        </>
      </SideDrawer>
    </Container>
  );
};

const Container = styled(Box)({
  flexGrow: 1,
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
});
